import { WORKFLOW_GOAL_EVENTS, WORKFLOW_GOAL_HOURS, type WorkflowGoalEvent } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { Field, Icon, Select } from "@sagot/ui";
import { IntegerInput } from "../../../workspace/shared";
import type { WorkflowGoal } from "../types";

const G = "engage.workflows.goal";
const HOURS_PER_DAY = 24;

interface GoalFieldProps {
  goal: WorkflowGoal | undefined;
  disabled: boolean;
  onChange: (goal: WorkflowGoal | undefined) => void;
}

/** The window as the author thinks of it: whole days read as days, anything else as hours. */
export function windowLabel(hours: number): { unit: "hours" | "days"; count: number } {
  return hours % HOURS_PER_DAY === 0 ? { unit: "days", count: hours / HOURS_PER_DAY } : { unit: "hours", count: hours };
}

/** Goal (optional): the event that counts as success and how long after the run starts it may happen. */
export function GoalField({ goal, disabled, onChange }: GoalFieldProps) {
  const { t } = useTranslation();
  const pick = (value: string) => {
    const event = WORKFLOW_GOAL_EVENTS.find((candidate) => candidate === value);
    onChange(event ? { event, within_hours: goal?.within_hours ?? WORKFLOW_GOAL_HOURS.default } : undefined);
  };
  const window = goal ? windowLabel(goal.within_hours) : null;
  return (
    <div className="space-y-3" data-testid="workflow-goal">
      <Field label={t(`${G}.label`)} hint={t(`${G}.hint`)}>
        <Select value={goal?.event ?? ""} disabled={disabled} onChange={(e) => { pick(e.target.value); }}>
          <option value="">{t(`${G}.none`)}</option>
          {WORKFLOW_GOAL_EVENTS.map((event: WorkflowGoalEvent) => <option key={event} value={event}>{t(`${G}.events.${event}`)}</option>)}
        </Select>
      </Field>
      {goal && window ? (
        <Field label={t(`${G}.within`)} hint={t(`${G}.withinHint`, { max: WORKFLOW_GOAL_HOURS.max })}>
          <div className="flex items-center gap-2">
            <IntegerInput min={WORKFLOW_GOAL_HOURS.min} max={WORKFLOW_GOAL_HOURS.max} className="w-28" disabled={disabled} value={goal.within_hours} onChange={(within_hours) => { onChange({ ...goal, within_hours }); }} />
            <span className="text-sm text-fg-muted">{t(`${G}.hours`)}</span>
            <span className="inline-flex items-center gap-1 text-xs text-fg-subtle" data-testid="goal-window">
              <Icon name="target" className="size-3.5" />
              {t(`${G}.window_${window.unit}`, { count: window.count })}
            </span>
          </div>
        </Field>
      ) : null}
    </div>
  );
}
